import { Component, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { FormControl } from '@angular/forms';

import {
  ImportJobTypeConstant,
  JobStatusConstant,
  OmsJobTypeConstant,
} from '@app/core/job-status/constants/job-status.constant';
import { IJobStatusUi } from '@app/core/job-status/models/job-status-ui.model';
import { JobStatusService } from '@app/core/job-status/services/job-status.service';
import { JobOutputDialogComponent } from '../job-output-dialog/job-output-dialog.component';

@Component({
  selector: 'cnc-job-status-details-popup',
  templateUrl: './job-status-details-popup.component.html',
  styleUrls: ['./job-status-details-popup.component.scss'],
})
export class JobStatusDetailsPopupComponent implements OnInit {
  // Form controls for filtering
  jobTypeControl = new FormControl('');
  statusControl = new FormControl('');
  searchControl = new FormControl('');

  constructor(
    private jobStatuService :JobStatusService,
    public dialogRef :MatDialogRef<JobStatusDetailsPopupComponent>,
    private router :Router,
    private dialog :MatDialog,
  ) {}

  jobs$ :Observable<IJobStatusUi[]> = this.jobStatuService.jobs$;
  jobStatusConstant = JobStatusConstant;

  // Filtered jobs based on controls
  filteredJobs$ :Observable<IJobStatusUi[]>;
  
  // Available filter options
  jobTypes$ :Observable<string[]>;
  statuses$ :Observable<string[]>;

  ngOnInit() :void {
    // Get unique job types and statuses from jobs
    this.jobTypes$ = this.jobs$.pipe(
      map(jobs => {
        const types = jobs.map(job => job.type).filter(Boolean);
        return [...new Set(types)].sort();
      })
    );

    this.statuses$ = this.jobs$.pipe(
      map(jobs => {
        const statuses = jobs.map(job => job.status).filter(Boolean);
        return [...new Set(statuses)].sort();
      })
    );

    // Set up filtered jobs observable
    this.filteredJobs$ = combineLatest([
      this.jobs$,
      this.jobTypeControl.valueChanges.pipe(startWith('')),
      this.statusControl.valueChanges.pipe(startWith('')),
      this.searchControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([jobs, jobTypeFilter, statusFilter, searchFilter]) => {
        return jobs.filter(job => {
          // Job type filter
          if (jobTypeFilter && job.type !== jobTypeFilter) {
            return false;
          }

          // Status filter
          if (statusFilter && job.status !== statusFilter) {
            return false;
          }

          // Search filter
          if (searchFilter) {
            const searchTerm = searchFilter.toLowerCase();
            const jobLabel = job.label?.toLowerCase() || '';
            const jobType = job.type?.toLowerCase() || '';
            const jobStatus = job.status?.toLowerCase() || '';
            
            if (!jobLabel.includes(searchTerm) && 
                !jobType.includes(searchTerm) && 
                !jobStatus.includes(searchTerm)) {
              return false;
            }
          }

          return true;
        });
      })
    );
  }

  closeDialog() {
    this.dialogRef.close('Pizza!');
  }

  isJobFinished(job :IJobStatusUi) :boolean {
    return job.status === JobStatusConstant.STATUS_FINISHED || 
           job.status === JobStatusConstant.STATUS_FAILED;
  }

  refreshJobs() :void {
    this.jobStatuService.getJobs();
  }

  /**
   * Check if a job is an import job that should have a link to the import page
   */
  isImportJob(job :IJobStatusUi) :boolean {
    const importJobTypes = [
      ImportJobTypeConstant.SALES_ORDER,
      ImportJobTypeConstant.IMPORT_V1_MASTER_VARIANT_DETAILS_JOB,
      ImportJobTypeConstant.IMPORT_CLIENT_SHIPPING_ADDRESSES_JOB,
    ];
    
    return importJobTypes.includes(job.type);
  }

  /**
   * Open a dialog to view the job's JSON output
   */
  viewJobOutput(job :IJobStatusUi) :void {
    this.dialog.open(JobOutputDialogComponent, {
      data: { job },
      width: '800px',
      maxHeight: '80vh',
    });
  }

  /**
   * Format job type names for better display in dropdowns
   */
  formatJobTypeName(jobType :string) :string {
    if (!jobType) return '';
    
    // If the job type is too long, truncate it intelligently
    if (jobType.length > 50) {
      // Try to find a good breaking point
      const parts = jobType.split(' ');
      let result = '';
      
      for (const part of parts) {
        if ((result + part).length > 47) {
          result += '...';
          break;
        }
        result += (result ? ' ' : '') + part;
      }
      
      return result || jobType.substring(0, 47) + '...';
    }
    
    return jobType;
  }

  redirectToJobStatus(job :IJobStatusUi = null) :void {
    if (job.id > 0) {
      if (job.type === ImportJobTypeConstant.BULK_SAVE_SHIPPING_LABELS) {
        if (job.input?.id) {
          this.router.navigateByUrl('/shipping/picklists/' + job.input.id);
        } else {
          this.router.navigateByUrl('/shipping/picklists');
        }
      } else if (
        job.type === ImportJobTypeConstant.GET_API2CART_PRODUCTS_JOB ||
        job.type === ImportJobTypeConstant.GET_ECART_API_PRODUCTS_JOB ||
        job.type === ImportJobTypeConstant.GET_QUICKBOOKS_PRODUCTS_JOB ||
        job.type === ImportJobTypeConstant.GET_SHOPIFY_PRODUCTS_JOB ||
        job.type === ImportJobTypeConstant.GET_TRACKSTAR_PRODUCTS_JOB
      ) {
        this.router.navigateByUrl('/channels/product-mapping');
      } else if (
        job.type === ImportJobTypeConstant.GET_ORDER_UPDATES_FROM_SELECT_ALL_JOB ||
        job.type ===
          ImportJobTypeConstant.UPDATE_ORDER_STATUS_FROM_SELECT_ALL_JOB ||
        job.type === ImportJobTypeConstant.UPDATE_TAG_FROM_SELECT_ALL_JOB ||
        job.type === ImportJobTypeConstant.SHIP_ORDERS_FROM_SELECT_ALL_JOB
      ) {
        this.router.navigateByUrl('/shipping/grid-posted-orders');
      } else if (Object.values(OmsJobTypeConstant).includes(job.type)) {
        this.closeDialog();
      } else if (
        job.type === ImportJobTypeConstant.IMPORT_V1_MASTER_VARIANT_DETAILS_JOB
      ) {
        this.router.navigateByUrl('/inventory/skus');
      } else if (
        job.type === ImportJobTypeConstant.IMPORT_CLIENT_SHIPPING_ADDRESSES_JOB
      ) {
        this.router.navigateByUrl(
          '/tools/imports/client-shipping-addresses?jobId=' + job.id,
        );
      } else if (job.type === ImportJobTypeConstant.SALES_ORDER) {
        // Only navigate to sales-orders import page for actual sales order import jobs
        this.router.navigateByUrl('/tools/imports/sales-orders?jobId=' + job.id);
      } else {
        // For all other job types, just close the dialog without navigation
        this.closeDialog();
        return;
      }
    }
    this.closeDialog();
  }
}
